/**
 * RouteTimeline - Visual journey timeline component
 * 
 * Shows: Origin -> [transport] -> City1 -> [transport] -> City2 -> [transport] -> Origin
 * 
 * Features:
 * - Horizontal scrollable on mobile with snap
 * - Click city to select it (preserves last day selection per city)
 * - Click transport icon to open edit modal
 * - Active city highlighted
 * - Dates shown under each city
 */

import { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TripContent, TransportType, formatDuration } from '../../services/api';
import { MapPinIcon, TransportIcon } from '../icons/TransportIcons';
import './RouteTimeline.css';

// =============================================================================
// TYPES
// =============================================================================

interface RouteTimelineProps {
    content: TripContent;
    selectedCityIdx: number;
    lastDayPerCity: Record<number, number>;
    onSelectCity: (cityIdx: number, dayIdx: number) => void;
    onEditRoutes: () => void;
    isEditable?: boolean;
    /** When set (e.g. 3), show only this many cities and "…" for the rest. Used in hero on desktop. */
    maxVisibleCities?: number;
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Format date range for display (e.g., "Jul 14 - Jul 18")
 */
function formatDateRange(start: string, end: string): string {
    const startDate = new Date(start);
    const endDate = new Date(end);
    
    const formatOptions: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
    const startStr = startDate.toLocaleDateString('en-US', formatOptions);
    const endStr = endDate.toLocaleDateString('en-US', formatOptions);
    
    // If same month, show "Jul 14 - 18"
    if (startDate.getMonth() === endDate.getMonth()) {
        return `${startStr} - ${endDate.getDate()}`;
    }
    
    return `${startStr} - ${endStr}`;
}

/**
 * Get default transport type based on position in trip
 * First leg defaults to flight, others to unknown
 */
function getDefaultTransportType(legIndex: number, totalLegs: number): TransportType {
    if (legIndex === 0 || legIndex === totalLegs) {
        return 'flight'; // First and return legs default to flight
    }
    return 'unknown';
}

// =============================================================================
// COMPONENT
// =============================================================================

export default function RouteTimeline({
    content,
    selectedCityIdx,
    lastDayPerCity,
    onSelectCity,
    onEditRoutes,
    isEditable = false,
    maxVisibleCities,
}: RouteTimelineProps) {
    const { t } = useTranslation();
    const scrollRef = useRef<HTMLDivElement>(null);
    
    const { cities, route } = content;
    const limit = maxVisibleCities != null && cities.length > maxVisibleCities ? maxVisibleCities : cities.length;
    const showEllipsis = maxVisibleCities != null && cities.length > maxVisibleCities;
    
    // Auto-scroll to selected city on mobile
    useEffect(() => {
        if (scrollRef.current) {
            const selectedElement = scrollRef.current.querySelector('.route-city.active');
            if (selectedElement) {
                selectedElement.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
            }
        }
    }, [selectedCityIdx]);
    
    // Handle city click - preserve last viewed day
    const handleCityClick = (cityIdx: number) => {
        const preservedDayIdx = lastDayPerCity[cityIdx] ?? 0;
        onSelectCity(cityIdx, preservedDayIdx);
    };
    
    // Handle transport click - open edit modal
    const handleTransportClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isEditable) {
            onEditRoutes();
        }
    };
    
    // Get transport type for a leg
    const getTransportType = (legIndex: number): TransportType => {
        if (route?.legs?.[legIndex]?.transport?.type) {
            return route.legs[legIndex].transport.type;
        }
        return getDefaultTransportType(legIndex, cities.length);
    };
    
    // Get return transport type
    const getReturnTransportType = (): TransportType => {
        if (route?.return?.transport?.type) {
            return route.return.transport.type;
        }
        return 'flight';
    };
    
    // Get transport duration for display
    const getTransportDuration = (legIndex: number): string | undefined => {
        const durationMin = route?.legs?.[legIndex]?.transport?.durationMin;
        return durationMin ? formatDuration(durationMin) : undefined;
    };
    
    // Get return transport duration
    const getReturnDuration = (): string | undefined => {
        const durationMin = route?.return?.transport?.durationMin;
        return durationMin ? formatDuration(durationMin) : undefined;
    };
    
    // Origin label
    const originLabel = route?.origin?.label || t('route.origin', 'Origin');
    
    // Check if return is enabled
    const hasReturn = route?.return?.enabled !== false;
    
    return (
        <div className="route-timeline-container">
            <div className="route-timeline" ref={scrollRef}>
                {/* Origin Point */}
                <div className="route-origin">
                    <div className="route-point origin">
                        <MapPinIcon size={20} />
                    </div>
                    <span className="route-label">{originLabel}</span>
                </div>
                
                {/* Cities with transport connectors (optionally limited + ellipsis) */}
                {cities.slice(0, limit).map((city, idx) => {
                    const isActive = idx === selectedCityIdx;
                    const transportType = getTransportType(idx);
                    const duration = getTransportDuration(idx);
                    const dateRange = formatDateRange(city.dateRange.start, city.dateRange.end);
                    
                    return (
                        <div key={city.id} className="route-segment">
                            {/* Transport connector */}
                            <div 
                                className={`route-connector ${isEditable ? 'editable' : ''}`}
                                onClick={handleTransportClick}
                                title={isEditable ? t('route.edit_transport', 'Edit transport') : undefined}
                            >
                                <div className="connector-line" />
                                <div className="transport-icon">
                                    <TransportIcon type={transportType} size={16} />
                                </div>
                                {duration && <span className="transport-duration">{duration}</span>}
                            </div>
                            
                            {/* City */}
                            <div 
                                className={`route-city ${isActive ? 'active' : ''}`}
                                onClick={() => handleCityClick(idx)}
                            >
                                <span className="city-name">{city.name}</span>
                                <span className="city-dates">{dateRange}</span>
                            </div>
                        </div>
                    );
                })}
                
                {/* Ellipsis when more cities than maxVisibleCities */}
                {showEllipsis && (
                    <div className="route-segment route-ellipsis">
                        <div className="route-connector">
                            <div className="connector-line" />
                        </div>
                        <div className="route-city route-city-ellipsis" aria-hidden>
                            <span className="city-name">…</span>
                        </div>
                    </div>
                )}
                
                {/* Return connector (if enabled) - hide when showing ellipsis (compact hero view) */}
                {hasReturn && !showEllipsis && (
                    <div className="route-segment return">
                        <div 
                            className={`route-connector ${isEditable ? 'editable' : ''}`}
                            onClick={handleTransportClick}
                            title={isEditable ? t('route.edit_transport', 'Edit transport') : undefined}
                        >
                            <div className="connector-line" />
                            <div className="transport-icon">
                                <TransportIcon type={getReturnTransportType()} size={14} />
                            </div>
                            {getReturnDuration() && (
                                <span className="transport-duration">{getReturnDuration()}</span>
                            )}
                        </div>
                        
                        {/* Return destination (origin) */}
                        <div className="route-origin return">
                            <div className="route-point origin">
                                <MapPinIcon size={20} />
                            </div>
                            <span className="route-label">{originLabel}</span>
                        </div>
                    </div>
                )}
            </div>
            
            {/* Edit button (if editable) */}
            {isEditable && (
                <button 
                    className="route-edit-btn"
                    onClick={onEditRoutes}
                    title={t('route.modify_routes', 'Modify Routes')}
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                    </svg>
                </button>
            )}
        </div>
    );
}

